import type { FC } from 'react'
import React, { memo } from 'react'
import { useSelector } from 'react-redux'
import { CommonLink } from '@frontend/pole-ui/lib/components/CommonLink'
import { Image, Loader } from '@/components/ui'
import { useLightboxPageContext } from '@/components/ui/Lightbox/constants'
import { useAddImageToLightboxOnload } from '@/components/ui/Lightbox/useAddImageToLightboxOnload'
import { isMobile } from '@/logic/app/reducer'
import type { RootState } from '@/redux/interfaces'
import { useLocation } from '@/router/hooks'
import { EJournalRoute } from '@/types'
import { formatLocalizedString } from '@/utils/formatUtils'
import { getMinioImagePath } from '@/utils/images'
import { createRouterLink } from '@/utils/url'
import { ESocialNetworks } from '../../constants'
import PublicationText from '../PublicationText'
import SocialShareBlock from '../SocialShareBlock'
import type { IPublicationMainFieldProps } from './interface'
import { messages } from './messages'
import './styles.scss'

const mapState = (state: RootState) => ({
  isDeviceMobile: isMobile()(state),
})

// TODO: рефакторинг, в техдолг - MOB-238
const PublicationMainField: FC<IPublicationMainFieldProps> = props => {
  const { publication, AsideNewsPreview, isLoading, blockId } = props
  const { origin, href } = useLocation()
  const { openLightbox } = useLightboxPageContext()

  const { lead, text = '', coverImage, source, title, tags = [] } = publication || {}

  const { isDeviceMobile } = useSelector(mapState)

  const shouldShowImage = !!(coverImage && coverImage.rectangleCover && coverImage.rectangleCover.length > 0)
  const shouldShowShareBlock = !!(title && title.length > 0)
  const shouldShowTags = tags.length > 0
  const imagePath = coverImage?.rectangleCover || ''
  const minioImagePath = shouldShowImage && !!imagePath ? getMinioImagePath({ src: imagePath }) : undefined
  useAddImageToLightboxOnload(minioImagePath ? { src: minioImagePath } : undefined)

  return (
    <section className="publicationMainField">
      <div className="publicationMainField__content">
        <Loader isVisible={isLoading} />

        {shouldShowImage && (
          <div onClick={() => openLightbox(imagePath)}>
            <Image src={imagePath} className="publicationMainField__coverImage" />
          </div>
        )}

        <div className="publicationMainField__textWrapper">
          {source && <p className="text_small publicationMainField__source">{formatLocalizedString(messages.source, { source })}</p>}

          {lead && <p className="publicationMainField__lead">{lead}</p>}

          {text && <PublicationText html={text} />}

          {shouldShowTags && (
            <ul className="publicationMainField__tags">
              {tags.map(tag => (
                <li className="text_super-small font-weight_light-bold publicationMainField__tag" key={tag.id}>
                  <CommonLink spanIfEmpty url={createRouterLink(EJournalRoute.TAG, { tagId: tag.id })}>
                    {tag.name}
                  </CommonLink>
                </li>
              ))}
            </ul>
          )}

          <div className="publicationMainField__social">
            <p className="h5 publicationMainField__socialHeader">{messages.social}</p>
            {shouldShowShareBlock && (
              <SocialShareBlock
                services={[ESocialNetworks.VKONTAKTE, ESocialNetworks.TELEGRAM, ESocialNetworks.VIBER, ESocialNetworks.WHATSAPP]}
                contentToShare={{
                  title,
                  description: lead,
                  url: href,
                  baseUrl: origin,
                  image: minioImagePath,
                }}
                theme={{ size: isDeviceMobile ? 'm' : 'l' }}
              />
            )}
          </div>
        </div>
      </div>

      {AsideNewsPreview}

      <span id={blockId} />
    </section>
  )
}

export default memo(PublicationMainField)
